/**
 * Performance trace middleware — adds Server-Timing + X-Structura-Perf headers.
 */
import { createMiddleware } from "@tanstack/react-start";
import {
  bindTraceToRequest,
  finalizeTrace,
  isPerfTraceEnabled,
  popTrace,
  pushTrace,
  toServerTimingHeader,
  traceToJson,
  type PerfTrace,
  type QueryRecord,
} from "@/lib/perf-trace";

export const perfTraceMiddleware = createMiddleware().server(async ({ request, next }) => {
  if (!isPerfTraceEnabled(request)) return next();

  const url = new URL(request.url);
  const trace: PerfTrace = {
    route: url.pathname,
    method: request.method,
    startedAt: performance.now(),
    totalMs: 0,
    ssrMs: 0,
    dbMs: 0,
    neonConnectMs: 0,
    cacheMs: 0,
    renderMs: 0,
    queryCount: 0,
    cacheHits: 0,
    cacheMisses: 0,
    queries: [] as QueryRecord[],
  };

  bindTraceToRequest(request, trace);
  pushTrace(trace);
  try {
    const result = await next();
    const response = result instanceof Response ? result : result.response;
    finalizeTrace(trace);
    const headers = new Headers(response.headers);
    headers.set("Server-Timing", toServerTimingHeader(trace));
    headers.set("X-Structura-Perf", JSON.stringify(traceToJson(trace)));
    headers.set("X-Query-Count", String(trace.queryCount));
    return new Response(response.body, {
      status: response.status,
      statusText: response.statusText,
      headers,
    });
  } finally {
    popTrace();
  }
});
